// ============================================================================
// LA LANGUE DE LA SESSION — une seule, pour toute la SPA.
//
// UTILITÉ : deux choses en dépendent et doivent parler la MÊME — le VOCABULAIRE (le mot que ce magasin
// emploie pour un champ d'article, `vocabulary.ts`) et les TEXTES de l'interface (`i18n.ts`). Tant que
// le vocabulaire était seul, la langue vivait chez lui ; elle a deux lecteurs, donc elle en est sortie.
//
// ⚠️ ELLE N'EST PAS TENUE ICI NON PLUS : c'est le moteur partagé (`@orgazone/i18n`) qui la porte,
//    depuis qu'il sert aussi les composants de `@orgazone/ui-web`. Deux porteurs — un pour l'interface,
//    un pour le vocabulaire — auraient fini par diverger, et l'écran aurait parlé deux langues à la
//    fois. Ce module reste la PORTE de cette application : il dit d'où vient la langue et ce qu'elle
//    entraîne, et il est le seul du web à lire la constante du contrat.
// ⚠️ ELLE VIENT DU COMPTE (SPEC §22.1, `bo_user.lang`), posée AVANT le premier rendu — le serveur la
//    sert avec l'utilisateur (`/auth/me`) pour qu'aucun écran ne s'affiche en français avant de se
//    corriger sous les yeux de l'utilisateur (§23.0).
// ⚠️ UNE LANGUE QUE LE CONTRAT NE CONNAÎT PAS RETOMBE SUR CELLE DE LA PLATEFORME — jamais une page qui
//    tombe : une langue retirée du contrat ne doit pas casser un compte qui la portait encore en base.
//    Le moteur applique cette règle lui-même, à partir des langues qu'on lui a déclarées.
// ============================================================================
import { DEFAULT_VOCABULARY_LANG, VOCABULARY_LANGS, type VocabularyLang } from '@orgazone/sync-contract';
import { configureI18n, currentLang as engineLang, onLangChange as engineOnLangChange, setLang } from '@orgazone/i18n';

/**
 * ⚠️ LA CONFIGURATION DU MOTEUR VIT ICI, au chargement de CE module, et pas dans celui des textes :
 *    tout chemin qui pose une langue passe par ici, et un chemin qui n'aurait pas importé les textes
 *    trouverait sinon un moteur ne connaissant qu'une langue — toutes les autres retomberaient sur le
 *    français, en silence. Les langues déclarées sont CELLES DU CONTRAT : une seconde liste finirait
 *    par diverger de la première.
 */
configureI18n({ referenceLang: DEFAULT_VOCABULARY_LANG, knownLangs: VOCABULARY_LANGS });

/** La langue vers laquelle tout retombe — celle d'un compte qui n'a rien choisi. */
export function platformLang(): VocabularyLang {
  return DEFAULT_VOCABULARY_LANG;
}

/**
 * LA LANGUE DU NAVIGATEUR — celle des écrans d'ENTRÉE, avant qu'on sache QUI se connecte.
 *
 * POURQUOI ELLE EXISTE. La langue du compte vient de `/auth/me` ; sur l'écran de connexion il n'y a pas
 * encore de compte, donc rien à lire. Le back-office entier se traduit et sa porte d'entrée parlait
 * français à tout le monde ; la décision utilisateur est que c'est le NAVIGATEUR qui tranche.
 *
 * ⚠️ ON LIT `languages`, PAS `language` : c'est une liste ORDONNÉE de préférences. Quelqu'un dont le
 *    navigateur annonce `es` puis `fr` veut l'espagnol — ne regarder que la première étiquette du
 *    système lui servirait le français au motif qu'il le comprend aussi.
 * ⚠️ ON NE GARDE QUE LA SOUS-ÉTIQUETTE PRIMAIRE, en minuscules : `en-US`, `EN-us` et `en` désignent la
 *    même langue, et BCP-47 n'impose aucune casse.
 * ⚠️ ELLE REND `null` PLUTÔT QUE DE DEVINER quand aucune langue connue n'est demandée — l'appelant
 *    retombe alors sur la plateforme, par le même chemin qu'un compte sans langue. Se taire vaut mieux
 *    que servir une langue que personne n'a demandée.
 * ⚠️ `navigator` PEUT NE PAS EXISTER (banc, rendu hors navigateur) : on rend `null`, on ne lève jamais.
 *    Une page de connexion qui tombe sur l'absence d'une COMMODITÉ serait un échange catastrophique.
 */
export function browserLang(): VocabularyLang | null {
  const nav = typeof navigator === 'undefined' ? null : navigator;
  const demandees = nav?.languages?.length ? nav.languages : (nav?.language ? [nav.language] : []);
  for (const etiquette of demandees) {
    // ⚠️ `?? ''` et non `!` : le compilateur a raison de douter d'un indice, et une étiquette vide
    //    n'appartient à aucune langue connue — elle tombe donc d'elle-même au tour suivant.
    const primaire = String(etiquette).toLowerCase().split('-')[0] ?? '';
    if ((VOCABULARY_LANGS as readonly string[]).includes(primaire)) return primaire as VocabularyLang;
  }
  return null;
}

/** S'abonner au changement de langue — pour les caches qui en dépendent. */
export function onLangChange(forget: () => void): void {
  engineOnLangChange(forget);
}

/** La langue en vigueur, lue AU RENDU — jamais capturée dans une constante de module. */
export function currentLang(): VocabularyLang {
  return engineLang() as VocabularyLang;
}

/** Pose la langue du compte ; une valeur inconnue (ou absente) retombe sur celle de la plateforme. */
export function setCurrentLang(next: string | null | undefined): void {
  setLang(next);
}
